import 'jsoneditor-react/es/editor.min.css';

import { yupResolver } from '@hookform/resolvers/yup';
import React, { FormEvent, useEffect, useState } from 'react';
import { Col, Form, Modal, Row } from 'react-bootstrap';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BiArrowBack, BiLogoJavascript, BiSolidMinusCircle, BiSolidPlusCircle } from 'react-icons/bi';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { useAppSelector } from '@/app/hook';
import CopyVariable from '@/components/common/CopyVariable';
import { PSButton } from '@/components/ui/button';
import { selectUseId } from '@/features/auth/auth.slice';
import KnowledgeBaseAlert from '@/features/bot/KnowledgeBaseAlert';
import { BlockType } from '@/interfaces/models/Knowledge/scenario.interface';

import HintBlock from '../../../../common/HintBlock';
import AddNoteButton from '../../AddNoteButton';
import NodeParentChanger from '../../NodeParentChanger';
import NoteForm, { getNoteSchema, INoteForm } from '../../NoteForm';
import ScenarioHeader from '../../ScenarioHeader';
import { MacBotActionKind } from '../../types';
import { containsSuspiciousCode, executeUserCode } from './executeUserCode';
import ResponseMapper from './ResponseMapper';
import TextScenarioDeleteButton from './scenario-delete-button';
import ScenarioJSInput from './scenario-js-input';
import ScenarioSide from './scenario-side';
import { ScenarioSimpleTextInput } from './scenario-simple-text-input';

interface IResponseParser {
  oldValue?: string | null;
  newValue: string;
  key: string;
}

interface IFormData extends INoteForm {
  title: string;
  context?: ContextType[];
  code?: string;
  responseParser?: IResponseParser[];
}

interface ContextType {
  key: string;
  value: string;
}

export const MacBotJSCodeForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
  config,
}: {
  setShow: (item: boolean) => void;
  itemData: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<any>;
  deleteScenarioBlock: (block: any) => void;
  changeScenarioBlock: (block: any) => Promise<any>;
  parentId: string | null;
  config: any;
  tree: any[];
}) => {
  const { t } = useTranslation();
  const userId = useAppSelector(selectUseId);

  const [isDelete, setIsDelete] = useState(true);
  const [showTesterModal, setShowTesterModal] = useState(false);
  const [rowNumbers, setRowNumbers] = useState<number[]>([]);
  const [showMapGuide, setShowMapGuide] = useState(false);
  const [testApiResponse, setTestApiResponse] = useState<{
    data?: any;
    status: 'error' | 'success';
    error?: any;
  } | null>(null);
  const [testLoading, setTestLoading] = useState(false);

  const schema = Yup.object({
    title: Yup.string().trim().required(t('McBotHttpForm/scenario_http_request_title_required')),
    context: Yup.array()
      .of(
        Yup.object().shape({
          key: Yup.string().trim().required(t('McBotHttpForm/scenario_http_request_header_key_required')),
          value: Yup.string().trim().required(t('McBotHttpForm/scenario_http_request_header_value_required')),
        })
      )
      .optional()
      .default([]),
    code: Yup.string().optional().default(''),
    responseParser: Yup.array()
      .of(
        Yup.object().shape({
          key: Yup.string().trim().optional().nullable(),
          oldValue: Yup.string().trim().optional().nullable(),
          newValue: Yup.string().trim().required(t('McBotHttpForm/scenario_http_request_map_value_required')),
        })
      )
      .optional()
      .default([]),
    ...getNoteSchema(t),
  });

  const cleanText = (text: string) => {
    if (text.trim() == '') {
      return text;
    }
    try {
      // Parse the fixed string to ensure it's valid JSON
      const jsonObj = JSON.parse(text);

      // Convert back to a properly formatted JSON string
      if (typeof jsonObj === 'number' || typeof jsonObj === 'bigint') {
        return jsonObj;
      } else {
        text = JSON.stringify(jsonObj, null, 2);
      }
    } catch {
      // JSON parse failed, keep original text
    }

    return text.trim();
  };

  const {
    register,
    formState: { errors },
    reset,
    watch,
    getValues,
    setValue,
    handleSubmit,
    control,
  } = useForm<IFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      title: itemData?.title ?? '',
      context: itemData?.context
        ? (Object.entries(itemData?.context).map(([key, value]) => ({
            key,
            value,
          })) as ContextType[])
        : [],
      code: itemData?.code ?? '',
      responseParser: [],
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: '',
          description: '',
          qc: false,
          author: userId,
        },
      ],
    },
  });

  const {
    fields: headerFields,
    append: headerAppend,
    remove: headerRemove,
  } = useFieldArray({
    control,
    name: 'context',
  });

  const { fields: noteFields } = useFieldArray({
    control,
    name: 'notes',
  });

  const [contextMenuList, setContextMenuList] = useState<any[]>([]);

  const handleJsonChange = (code: any) => {
    setValue('code', code ?? '');
  };

  const deleteHandler = () => {
    deleteScenarioBlock(itemData);
    setShow(false);
  };

  const onSubmit = async (data: any) => {
    const context = data?.context?.reduce((obj: any, item: any) => {
      obj[item.key] = item.value;
      return obj;
    }, {});
    const code = cleanText(data.code);

    const result = await containsSuspiciousCode(code as string, t);

    if (result !== null) {
      toast.warn(result);
      return;
    }
    if (itemData) {
      const _data = {
        ...itemData,
        ...data,
        id: itemData.id,
        type: BlockType.JsCode,
        parent: itemData.parent,
        context: context,
        title: data.title,
        code: code,
        responseParser:
          data.responseParser?.map((dr: IResponseParser) => {
            return {
              newValue: dr.newValue,
              oldValue: dr.oldValue,
            };
          }) ?? [],
      };
      changeScenarioBlock(_data).then(() => {
        reset();
        setShow(false);
      });
    } else {
      const _data = {
        ...data,
        type: BlockType.JsCode,
        parent: parentId,
        context: context,
        title: data.title,
        code: code,
        responseParser:
          data.responseParser?.map((dr: IResponseParser) => {
            return {
              newValue: dr.newValue,
              oldValue: dr.oldValue,
            };
          }) ?? [],
      };
      createScenarioBlock(_data).then(() => {
        reset();
        setShow(false);
      });
    }
  };

  const handleExecuteTest = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setTestLoading(true);

    const formData = new FormData(e.currentTarget);

    const contextVariables = formData.getAll('context[key]');
    const contextValues = formData.getAll('context[value]');
    const nonVariableContext = getValues('context')?.filter(context => !context.value.startsWith('$:var_'));

    const code = getValues('code');

    const object: Record<string, any> = {
      context: [
        ...(nonVariableContext as ContextType[]),
        ...contextVariables.map((variable, index) => {
          return {
            key: cleanText(variable as string),
            value: cleanText(contextValues[index] as string),
          };
        }),
      ],
    };

    const context: Record<string, any> = object?.context?.reduce((acc: any, curr: any) => {
      acc[curr.key] = curr.value;
      return acc;
    }, {} as Record<string, any>);

    executeUserCode(code as string, context, 250, t).then((res: any) => {
      if (res.status == 'success') {
        setTestApiResponse({
          status: res.status,
          data: res.result,
        });
      } else {
        setTestApiResponse({
          status: res.status,
          data: res.error,
        });
      }
    });

    setTestLoading(false);
  };

  useEffect(() => {
    if (!itemData) {
      setIsDelete(false);
    } else {
      reset({
        title: itemData?.title ?? '',
        context: itemData?.context
          ? (Object.entries(itemData?.context).map(([key, value]) => ({
              key,
              value,
            })) as ContextType[])
          : [],
        code: itemData?.code ?? '',
        responseParser:
          itemData.responseParser?.map((dr: IResponseParser) => {
            return {
              newValue: dr.newValue,
              oldValue: dr.newValue,
              key: dr.key,
            };
          }) ?? [],
        notes: itemData?.notes,
      });
    }
  }, [itemData]);

  useEffect(() => {
    return () => {
      setTestApiResponse(null);
    };
  }, [showTesterModal]);

  useEffect(() => {
    if (config.metaData.variables) {
      setContextMenuList([...config.metaData.variables]);
    }
  }, [config]);

  return (
    <React.Fragment>
      <ScenarioSide size="lg" onSubmit={handleSubmit(onSubmit)}>
        <ScenarioHeader
          type={MacBotActionKind.ShowJSCode}
          onClose={() => {
            reset();
          }}
        />

        <div className="md:tw-p-6 tw-p-4 tw-bg-gray-50 tw-flex tw-flex-col tw-gap-y-4 tw-w-full">
          <Row>
            <Form.Group className="tw-w-full">
              <Form.Label htmlFor="" className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                {t('McBotHttpForm/key4')}
              </Form.Label>
              <Form.Control
                className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                maxLength={30}
                max={30}
                type="text"
                isInvalid={!!errors.title}
                {...register('title')}
              />
              <HintBlock texts={['McBotHttpForm/maxlenght']} />
              <Form.Control.Feedback type="invalid" className="d-block font-poppins tw-mt-3 tw-text-sm">
                {errors.title?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row>
            <Col xs={12}>
              <div className="tw-flex tw-flex-col tw-gap-3">
                <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
                  <div className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">Context</div>
                </div>

                {headerFields.map((field, index) => (
                  <Row key={field.id} className={`tw-mt-2 tw-items-start ${index + 1 < headerFields.length ? 'mb-3' : ''} `}>
                    <Col md={10} xs={9}>
                      <Row className="gap-3">
                        <Col xs={12}>
                          <Form.Group className="tw-relative">
                            <Form.Control
                              className="tw-overflow-visible tw-relative tw-flex-grow tw-overflow-x-auto tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-solid tw-border-gray-300  "
                              placeholder={t('JSCode/ContextKey')}
                              type="text"
                              isInvalid={!!errors.context?.[index]?.key?.message}
                              {...register(`context.${index}.key` as const)}
                            />
                            <Form.Control.Feedback type="invalid" className="tw-block">
                              {errors.context?.[index]?.key?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col xs={12}>
                          <Form.Group className="tw-relative">
                            <ScenarioSimpleTextInput
                              placeholder={t('JSCode/ContextValue')}
                              setValue={text => {
                                setValue(`context.${index}.value`, text);
                              }}
                              value={getValues(`context.${index}.value`)}
                              contextMenuList={contextMenuList}
                              isInvalid={!!errors.context?.[index]?.value}
                            />
                            <Form.Control.Feedback type="invalid" className="tw-block">
                              {errors.context?.[index]?.value?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Col>

                    <Col md={1} xs={2} className="tw-self-stretch">
                      <PSButton
                        id="btn-js-code-remove"
                        variant="danger"
                        type="button"
                        onClick={() => headerRemove(index)}
                        icon={<BiSolidMinusCircle size={20} />}
                      />
                    </Col>
                  </Row>
                ))}

                <PSButton
                  id="btn-js-code-append"
                  variant="outline"
                  type="button"
                  onClick={() => headerAppend({ key: '', value: '' })}
                  icon={<BiSolidPlusCircle className="tw-text-gray-400" />}
                  title={t('JSCode/Context')}
                />
              </div>
            </Col>
          </Row>

          <div className="h-[0px] border border-gray-200"></div>

          <KnowledgeBaseAlert
            texts={[
              'JSCode/recommendation-2',
              'JSCode/recommendation-3',
              'JSCode/recommendation-4',
              'JSCode/recommendation-5',
              'JSCode/recommendation-6',
              'JSCode/recommendation-7',
            ]}
          />

          <div className="h-[0px] border border-gray-200"></div>

          <div className="tw-flex tw-flex-col tw-gap-3">
            <PSButton id="btn-js-code-show-map-guide" variant="outline" type="button" onClick={() => setShowMapGuide(true)} title={t('JSCode/Sample')} />
          </div>

          {/* Json editor */}
          <Row>
            <Col xs={12}>
              <div className="tw-flex tw-flex-col">
                <div className="tw-flex tw-justify-between tw-items-center tw-w-full tw-mb-3">
                  <span className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">{t('JSCode/YourJSCode')}</span>
                </div>

                <div id="json-body">
                  <Controller
                    control={control}
                    name="code"
                    render={({ field: { value, onChange } }) => (
                      <div>
                        <ScenarioJSInput
                          setValue={text => {
                            handleJsonChange(text);
                          }}
                          placeholder=""
                          value={getValues('code') ?? ''}
                          contextMenuList={contextMenuList}
                          isInvalid={!!errors.code}
                        />
                        {rowNumbers.map((number, index) => (
                          <div key={index}>{number}</div>
                        ))}
                      </div>
                    )}
                  />
                  {!!errors.code && (
                    <Form.Control.Feedback className="tw-block py-2" type="invalid">
                      {errors.code?.message}
                    </Form.Control.Feedback>
                  )}
                </div>

                <PSButton
                  id="btn-js-code-show-tester-modal"
                  className="tw-mt-3"
                  variant="secondary"
                  type="button"
                  onClick={() => setShowTesterModal(true)}
                  title={t('JSCode/TestCode')}
                />
              </div>
            </Col>
          </Row>

          <div className="h-[0px] border border-gray-200"></div>
          <Row className="tw-items-center tw-flex">
            <Col xs={12}>
              <Form.Group>
                <Form.Label htmlFor="" className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                  {t('JSCode/VariableText')}
                </Form.Label>
                <Controller name="title" control={control} render={({ field: { value } }) => <CopyVariable text={value + '_js_code'} noTitle />} />
              </Form.Group>
            </Col>
          </Row>

          {/* Response mapper */}
          <ResponseMapper {...{ register, control, watch, formState: { errors } }} />
        </div>
        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            <TextScenarioDeleteButton itemData={itemData} tree={tree} deleteHandler={deleteHandler} />

            {itemData?.id && <NodeParentChanger tree={tree} currentId={itemData.id} />}
            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <PSButton
              id="btn-js-code-submit"
              type="submit"
              className="tw-h-11 tw-px-5 tw-py-3 tw-bg-primary tw-rounded-[99px] tw-justify-center tw-items-center tw-inline-flex tw-z-20"
              disabled={loading}
              isLoading={loading}
              title={t('MacBotResponseForm/btn-02')}
            />
          </div>
        </div>
        <NoteForm noteFormMethods={{ register, watch, errors, fields: noteFields }} />
      </ScenarioSide>

      <Modal show={showMapGuide} onHide={() => setShowMapGuide(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('McBotHttpForm/key11')}</Modal.Title>
        </Modal.Header>

        <Modal.Body className="tw-p-6">
          {/* Section principale avec les deux colonnes */}
          <div className="tw-mb-6">
            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-5 tw-gap-4 tw-min-h-[250px]">
              {/* Colonne gauche : Variables injectées (2/5 de l'espace) */}
              <div className="lg:tw-col-span-2 tw-flex tw-flex-col">
                <h6 className="tw-text-gray-600 tw-text-sm tw-font-semibold tw-mb-3 tw-font-['Lexend']">Input Variables</h6>
                <div className="tw-flex-1 tw-p-4 tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-200">
                  <pre className="tw-text-gray-700 tw-text-[10px] tw-font-mono tw-whitespace-pre-wrap tw-m-0">
                    {`{
  "userName": "Alice",
  "isPremiumUser": true,
  "cartTotal": 49.99
}`}
                  </pre>
                </div>
              </div>

              {/* Colonne droite : Code JS (3/5 de l'espace) */}
              <div className="lg:tw-col-span-3 tw-flex tw-flex-col">
                <h6 className="tw-text-gray-600 tw-text-sm tw-font-semibold tw-mb-3 tw-font-['Lexend']">JavaScript Code Example</h6>
                <div className="tw-flex-1 tw-p-4 tw-bg-white tw-rounded-lg tw-border tw-border-gray-300 tw-overflow-auto">
                  <pre className="tw-text-gray-700 tw-text-[10px] tw-font-mono tw-whitespace-pre-wrap tw-m-0">
                    {`if (isPremiumUser && cartTotal > 40) {
  return \`Thanks \${userName}, you qualify for free shipping!\`;
} else {
  return \`Hello \${userName}, add more to your cart for free shipping.\`;
}`}
                  </pre>
                </div>
              </div>
            </div>
          </div>

          {/* Section résultat */}
          <div className="tw-border-t tw-pt-4">
            <h6 className="tw-text-gray-600 tw-text-sm tw-font-semibold tw-mb-3 tw-font-['Lexend']">{t('JSCode/SampleReturn')}</h6>
            <div className="tw-p-4 tw-bg-green-50 tw-rounded-lg tw-border tw-border-green-200">
              <pre className="tw-text-green-700 tw-text-sm tw-font-mono tw-m-0">{'Thanks Alice, you qualify for free shipping!'}</pre>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showTesterModal} onHide={() => setShowTesterModal(false)} centered>
        <Modal.Header className="tw-p-3 tw-self-stretch tw-bg-gray-50 tw-rounded-tl-2xl tw-rounded-tr-2xl tw-justify-between tw-items-center tw-flex tw-w-full tw-border-0">
          <div className="tw-justify-start tw-items-center tw-gap-2 tw-flex">
            <button
              id="btn-arrow-back"
              onClick={() => setShowTesterModal(false)}
              className="tw-h-11 tw-p-2.5 tw-bg-gray-100 tw-rounded-[99px] tw-border-none tw-justify-start tw-items-center tw-gap-3 tw-flex tw-flex-col"
            >
              <div className="tw-w-6 tw-h-6 relative">
                <BiArrowBack size={22} className="tw-text-gray-800" />
              </div>
            </button>
            <div className="tw-h-11 tw-p-2.5 tw-bg-gray-100 tw-rounded-[99px] tw-justify-start tw-items-center tw-gap-3 tw-inline-flex">
              <BiLogoJavascript className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />
              <div className="tw-text-gray-800 tw-text-base tw-font-semibold tw-font-['Lexend'] tw-leading-normal">{t('JSCode/Title')}</div>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="md:tw-p-6 tw-p-4 tw-bg-gray-50 tw-flex tw-flex-col tw-gap-y-4 tw-w-full">
          <Form onSubmit={handleExecuteTest}>
            {/* Headers */}
            {(getValues('context')?.length as number) > 0 && (
              <div className="tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7 tw-mb-3">{t('JSCode/Context')}</div>
            )}
            {getValues('context')?.map((item, index) => {
              return (
                <Row key={index} className="g-3 mb-4">
                  <Col md={12} xs={12}>
                    <Form.Group>
                      <Form.Control
                        placeholder={t('JSCode/ContextKey')}
                        defaultValue={item.key}
                        name={`context[key]`}
                        readOnly
                        className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12} xs={12}>
                    <Form.Group>
                      <Form.Control
                        placeholder={t('JSCode/ContextValue')}
                        defaultValue={item.value}
                        type="text"
                        name={`context[value]`}
                        required
                        className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                      />
                    </Form.Group>
                  </Col>
                  <hr />
                </Row>
              );
            })}

            {testApiResponse && testApiResponse.status && (
              <div>
                <div className="tw-h-7 tw-justify-start tw-items-center tw-gap-4 tw-inline-flex tw-mb-4">
                  <div className="tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">{t('JSCode/CodeStatus')}</div>

                  {testApiResponse.status && (
                    <React.Fragment>
                      {testApiResponse.status == 'success' ? (
                        <div className="tw-px-2 tw-bg-emerald-500 tw-rounded tw-justify-center tw-items-center tw-gap-2.5 tw-flex">
                          <div className="tw-text-white tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">{t('JSCode/success')}</div>
                        </div>
                      ) : (
                        <div className="tw-px-2 tw-bg-red-400 tw-rounded tw-justify-center tw-items-center tw-gap-2.5 tw-flex">
                          <div className="tw-text-white tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">{t('JSCode/error')}</div>
                        </div>
                      )}
                    </React.Fragment>
                  )}
                </div>

                <div className="tw-mb-4">
                  <div className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none tw-mb-3">Response</div>

                  <pre className="tw-border tw-border-solid tw-border-gray-300 tw-max-h-48 tw-rounded-md tw-p-2 tw-overflow-auto">
                    {testApiResponse?.data === undefined && 'undefined'}

                    {testApiResponse?.data === null && 'null'}

                    {testApiResponse?.data && JSON.stringify(testApiResponse.data, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            <div className="text-end">
              <PSButton
                id="btn-js-code-test"
                type="submit"
                isLoading={testLoading}
                className="tw-px-[17px] tw-py-2.5 disabled:tw-bg-gray-300 tw-rounded-[99px] tw-shadow tw-justify-center tw-items-center tw-flex tw-ml-auto"
              >
                <div className="tw-text-white tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{t('JSCode/BtnTest')}</div>
              </PSButton>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};
